/**
 * Plan & budget — template budgets carved out of the plan.
 *
 * The plan is the pool: a monthly fee that buys an allowance of pages. A
 * template budget is a share of that pool, set in money because that is how
 * admins think about it, and measured in pages because that is what the plan
 * actually meters.
 *
 * Pages are the unit; money is derived from them at ONE rate, the plan rate:
 *
 *   plan rate      fee / included pages — what each included page is worth.
 *                  A template's spend is its pages at this rate, and a budget
 *                  buys amount / plan rate pages, so budgets that add up to
 *                  exactly the fee add up to exactly the allowance.
 *
 * Budgets that add up to *more* than the fee promise more pages than the
 * allowance holds. Those pages, converted at the same plan rate, are the ones
 * that would be billed as overage ("£500 over plan" on Starter, at £1 a page,
 * is 500 pages), and only then are they priced at the overage rate (500 pages
 * x £0.06 = £30). Converting the excess *money* straight into pages at the
 * overage rate mixed two rates, and on Starter was seventeen times out.
 *
 * Whether a template is over budget is likewise decided in pages (used vs
 * budgeted); the money shown beside it is derived from them.
 *
 * Every amount here is in the plan's own currency (GBP). The page converts
 * for display only, so flipping the currency toggle never rewrites a budget.
 */
import { convertAmount } from './billing'
import { apportion } from './apportion'

export { apportion }

// ---------------------------------------------------------------------------
// The pool
// ---------------------------------------------------------------------------

/** Alert threshold a new budget starts with: warn at 80% spent. */
export const DEFAULT_ALERT_AT = 80
export const ALERT_OPTIONS = [50, 75, 80, 90, 100]

const round2 = n => Math.round((Number(n) || 0) * 100) / 100

/** What the plan puts on the table. Null when the plan has no fee to budget against. */
export function budgetPool(plan) {
  const fee = Number(plan?.monthly_fee) || 0
  if (fee <= 0) return null
  const included = Number(plan?.usage?.pages_included ?? plan?.included_pages) || 0
  const overageRate = Number(plan?.overage_per_page) || 0
  return {
    planId: plan?.id || null,
    planName: plan?.name || 'Your plan',
    fee,
    included,
    overageRate,
    // An allowance of zero pages has no per-page worth; spend is then read
    // at the overage rate, which is what every page would cost.
    planRate: included > 0 ? fee / included : overageRate,
    currency: plan?.currency || { code: 'GBP', symbol: '£' },
  }
}

/** Pages a budget of `amount` buys at the plan rate. */
export function pagesForAmount(amount, pool) {
  if (!pool || !(pool.planRate > 0)) return 0
  return Math.floor((Number(amount) || 0) / pool.planRate)
}

/** A template's spend: its pages at the plan rate. */
export function spendForPages(pages, pool) {
  if (!pool) return 0
  return round2((Number(pages) || 0) * pool.planRate)
}

/**
 * Pages that `excess` money is worth at the plan rate: the pages a budget
 * promises beyond the allowance. Same rate the spend is valued at.
 */
export function overagePagesFor(excess, pool) {
  if (!pool || !(pool.planRate > 0) || !(excess > 0)) return 0
  return Math.round(excess / pool.planRate)
}

/** What `pages` beyond the allowance would be billed as overage. */
export function overageCostFor(pages, pool) {
  if (!pool || !(pool.overageRate > 0) || !(pages > 0)) return 0
  return round2(pages * pool.overageRate)
}

// ---------------------------------------------------------------------------
// Allocation
// ---------------------------------------------------------------------------

/** Sum of every budget that has an amount. */
export function totalAllocated(budgets) {
  return round2(Object.values(budgets || {}).reduce((sum, b) => sum + (Number(b?.amount) || 0), 0))
}

/**
 * The cheapest published tier whose fee covers `amount`, if it is not the
 * current one. The answer to "so what should I do about being over plan".
 */
export function planThatCovers(amount, plan) {
  const catalogue = (plan?.catalogue || []).filter(p => Number(p.monthly_fee) > 0)
  const fits = catalogue
    .filter(p => Number(p.monthly_fee) >= amount)
    .sort((a, b) => a.monthly_fee - b.monthly_fee)[0]
  if (!fits || fits.id === plan?.id) return null
  return { id: fits.id, name: fits.name, fee: Number(fits.monthly_fee), includedPages: Number(fits.included_pages) || 0 }
}

/**
 * Where the budgets stand against the plan.
 *
 * `overPlan` is the headline: how much of what has been promised to templates
 * the plan does not pay for.
 */
export function summariseAllocation(budgets, pool, plan = null) {
  const allocated = totalAllocated(budgets)
  const count = Object.values(budgets || {}).filter(b => Number(b?.amount) > 0).length
  if (!pool) {
    return { allocated, count, fee: 0, unallocated: 0, overPlan: 0, overPct: 0, usedPct: 0, extraPages: 0, extraPagesCost: 0, fitScale: 1, covers: null }
  }
  const overPlan = round2(Math.max(0, allocated - pool.fee))
  const extraPages = overagePagesFor(overPlan, pool)
  return {
    allocated,
    count,
    fee: pool.fee,
    unallocated: round2(Math.max(0, pool.fee - allocated)),
    overPlan,
    overPct: pool.fee > 0 ? (overPlan / pool.fee) * 100 : 0,
    usedPct: pool.fee > 0 ? (allocated / pool.fee) * 100 : 0,
    extraPages,
    // What those pages would cost if they were billed as overage.
    extraPagesCost: overageCostFor(extraPages, pool),
    // Multiply every budget by this to land exactly on the fee.
    fitScale: allocated > 0 ? Math.min(1, pool.fee / allocated) : 1,
    covers: overPlan > 0 ? planThatCovers(allocated, plan) : null,
  }
}

/**
 * What the allocation becomes if one template's budget changes to `amount`
 * (0 or null clears it). The editor renders this live as the admin types.
 */
export function previewChange(budgets, templateId, amount, pool, plan = null) {
  const key = String(templateId)
  const next = { ...(budgets || {}) }
  const value = Number(amount) || 0
  if (value > 0) next[key] = { ...(next[key] || {}), amount: value }
  else delete next[key]

  const before = summariseAllocation(budgets, pool, plan)
  const after = summariseAllocation(next, pool, plan)
  const others = round2(after.allocated - value)
  return {
    before,
    after,
    others,
    // Most this template can take without going over plan.
    headroom: pool ? round2(Math.max(0, pool.fee - others)) : 0,
    // This change on its own pushes the total over plan, or deeper into it.
    pushesOver: after.overPlan > before.overPlan,
    // The overage this change itself is responsible for.
    addedOver: round2(Math.max(0, after.overPlan - before.overPlan)),
    // One budget bigger than the whole plan is its own, sharper, message.
    exceedsPlanAlone: pool ? value > pool.fee : false,
  }
}

/**
 * Scale every budget down so the total is exactly the fee.
 *
 * Whole currency units, largest-remainder rounding: each budget keeps its
 * share of the total, nothing is rounded into a sum that is a pound off, and
 * a budget that is already inside the plan is never touched.
 */
export function fitToPlan(budgets, pool) {
  const entries = Object.entries(budgets || {}).filter(([, b]) => Number(b?.amount) > 0)
  const total = entries.reduce((sum, [, b]) => sum + Number(b.amount), 0)
  if (!pool || total <= pool.fee) return { ...(budgets || {}) }

  const shares = apportion(entries.map(([key, b]) => ({ key, weight: Number(b.amount) })), Math.floor(pool.fee))
  const out = { ...(budgets || {}) }
  for (const [key, b] of entries) out[key] = { ...b, amount: shares.get(key) }
  return out
}

/**
 * A starting allocation: the whole plan fee shared out by each template's
 * projected spend this cycle. Busy templates get room in proportion to how
 * busy they are, idle ones get none, and the total lands exactly on the plan.
 * Alert thresholds already set are kept.
 */
export function distributeByUsage(rows, pool, existing = {}) {
  if (!pool) return { ...(existing || {}) }
  const busy = (rows || []).filter(r => r.projected > 0 && !r.removed)
  if (!busy.length) return { ...(existing || {}) }
  const shares = apportion(busy.map(r => ({ key: String(r.id), weight: r.projected })), Math.floor(pool.fee))
  const out = {}
  for (const [key, amount] of shares) {
    if (amount > 0) out[key] = { amount, alertAt: existing?.[key]?.alertAt || DEFAULT_ALERT_AT }
  }
  return out
}

// ---------------------------------------------------------------------------
// One template against its budget
// ---------------------------------------------------------------------------

/** Straight-line run rate to the end of the cycle — the same rule the plan panel uses. */
export function projectToCycleEnd(value, cycle) {
  const elapsed = Math.max(1, Number(cycle?.days_elapsed) || 1)
  const total = Math.max(elapsed, Number(cycle?.days_total) || elapsed)
  return round2(((Number(value) || 0) / elapsed) * total)
}

export const STATUS = {
  unset: { key: 'unset', label: 'No budget', rank: 0 },
  ok: { key: 'ok', label: 'On track', rank: 1 },
  watch: { key: 'watch', label: 'Past alert', rank: 2 },
  risk: { key: 'risk', label: 'At risk', rank: 3 },
  over: { key: 'over', label: 'Over budget', rank: 4 },
}

/**
 * Worst condition wins: already over, then heading over by the cycle end,
 * then past the alert threshold.
 *
 * Decided in pages when the caller has them (`pages` used, `budgetPages` the
 * budget buys at the plan rate, `projectedPages` at the cycle end): pages are
 * what the plan meters and money is only their value at one rate. With no
 * pages, the same comparison is made in money.
 *
 * `projectionReliable: false` (the first days of a cycle, when a run rate is
 * one big upload extrapolated) withholds "At risk": the projection is not
 * evidence yet. Already being over budget, or past the alert, is measured
 * spend and still counts.
 */
export function budgetStatus({
  amount, spent, projected, alertAt = DEFAULT_ALERT_AT,
  pages, budgetPages, projectedPages, projectionReliable = true,
}) {
  const budget = Number(amount) || 0
  if (budget <= 0) return STATUS.unset
  const inPages = Number.isFinite(pages) && Number(budgetPages) > 0
  const used = inPages ? pages : spent
  const cap = inPages ? Number(budgetPages) : budget
  const heading = inPages ? projectedPages : projected
  const EPS = 1e-9
  if (used >= cap - EPS) return STATUS.over
  if (projectionReliable !== false && heading > cap + EPS) return STATUS.risk
  if (used >= cap * (alertAt / 100) - EPS) return STATUS.watch
  return STATUS.ok
}

/**
 * One row per template: every active template (so an idle one can be given a
 * budget before it gets busy) plus any template that spent this cycle but is
 * no longer in the list.
 *
 * @param templates  rows from /api/templates ({ id, name })
 * @param spend      explorer template items ({ key, label, pages, cost, requests })
 * @param budgets    { [templateId]: { amount, alertAt } }
 */
export function buildBudgetRows({ templates = [], spend = [], budgets = {}, pool, cycle, projectionReliable = true }) {
  const byId = new Map()
  for (const t of templates) {
    if (t?.id == null) continue
    byId.set(String(t.id), { id: String(t.id), name: t.name || `Template #${t.id}`, pages: 0, requests: 0, azureUsd: 0, inactive: t.is_active === false })
  }
  for (const s of spend) {
    if (s?.templateId == null) continue
    const id = String(s.templateId)
    const row = byId.get(id) || { id, name: s.label || `Template #${id}`, pages: 0, requests: 0, azureUsd: 0, removed: true }
    row.pages += Number(s.pages) || 0
    row.requests += Number(s.requests) || 0
    row.azureUsd += Number(s.cost) || 0
    byId.set(id, row)
  }
  // A budget for a template that has since been deleted still counts against
  // the plan, so it stays visible — and removable.
  for (const id of Object.keys(budgets || {})) {
    if (!byId.has(id)) byId.set(id, { id, name: `Template #${id}`, pages: 0, requests: 0, azureUsd: 0, removed: true })
  }

  return [...byId.values()].map(row => {
    const b = budgets?.[row.id] || null
    const amount = Number(b?.amount) || 0
    const alertAt = Number(b?.alertAt) || DEFAULT_ALERT_AT
    const spent = spendForPages(row.pages, pool)
    const projected = projectToCycleEnd(spent, cycle)
    // The budget in exact pages (amount / plan rate), so the status is a
    // comparison of pages with pages and not of two rounded money figures.
    const budgetPagesExact = pool && pool.planRate > 0 ? amount / pool.planRate : 0
    const status = budgetStatus({
      amount, spent, projected, alertAt,
      pages: row.pages,
      budgetPages: budgetPagesExact,
      projectedPages: projectToCycleEnd(row.pages, cycle),
      projectionReliable,
    })
    return {
      ...row,
      amount,
      alertAt,
      spent,
      projected,
      status,
      pctSpent: amount > 0 ? (spent / amount) * 100 : null,
      pctProjected: amount > 0 ? (projected / amount) * 100 : null,
      remaining: amount > 0 ? round2(amount - spent) : null,
      budgetPages: pagesForAmount(amount, pool),
      updatedAt: b?.updatedAt || null,
    }
  })
}

/** The spend that never reaches a template budget: dynamic extraction, set-up jobs, deleted requests. */
export function untrackedSpend(spend = [], pool) {
  const pages = spend.filter(s => s?.templateId == null).reduce((sum, s) => sum + (Number(s.pages) || 0), 0)
  return { pages, spent: spendForPages(pages, pool) }
}

/** Series hues without the reds and ambers that mean "over" and "warning" on this page. */
const SAFE_SERIES = [0, 1, 3, 5, 12, 9, 7, 8, 10, 15]

/** Colour for the `index`th budget segment, from the chart theme's palette. */
export function segmentColor(chart, index) {
  const series = chart?.series || []
  if (!series.length) return '#3b82f6'
  return series[SAFE_SERIES[index % SAFE_SERIES.length] % series.length]
}

export const SORTS = {
  status: (a, b) => (b.status.rank - a.status.rank) || (b.spent - a.spent),
  spent: (a, b) => b.spent - a.spent,
  budget: (a, b) => b.amount - a.amount,
  name: (a, b) => a.name.localeCompare(b.name),
}

export const FILTERS = [
  { value: 'all', label: 'All', test: () => true },
  { value: 'budgeted', label: 'Budgeted', test: r => r.amount > 0 },
  { value: 'attention', label: 'Needs attention', test: r => r.status.rank >= STATUS.watch.rank },
  { value: 'unbudgeted', label: 'No budget', test: r => r.amount <= 0 },
]

// ---------------------------------------------------------------------------
// Money in and out of the editor
// ---------------------------------------------------------------------------

/** Plan-currency amount → the currency on screen. */
export function toDisplay(amount, nativeCode = 'GBP', displayCode = nativeCode) {
  return convertAmount(amount, nativeCode, displayCode)
}

/** The currency on screen → plan currency, for storage. */
export function fromDisplay(amount, nativeCode = 'GBP', displayCode = nativeCode) {
  return convertAmount(amount, displayCode, nativeCode)
}

/**
 * Read what an admin typed. Forgiving about the things people actually type —
 * a currency symbol, thousands separators, "5k" — and strict about the rest:
 * anything that is not a non-negative number is null, never a silent zero.
 */
export function parseMoneyInput(text) {
  if (text === null || text === undefined) return null
  const s = String(text).trim().toLowerCase().replace(/[£$€,\s]/g, '')
  if (!s) return null
  const m = s.match(/^(\d+(?:\.\d+)?|\.\d+)(k|m)?$/)
  if (!m) return null
  const mult = m[2] === 'k' ? 1_000 : m[2] === 'm' ? 1_000_000 : 1
  const n = Number(m[1]) * mult
  return Number.isFinite(n) ? round2(n) : null
}

/** Rows for the budget export. */
export function buildBudgetCsvRows(rows, pool, summary) {
  const header = ['Template', 'Budget', 'Alert at %', 'Spent this cycle', 'Projected', 'Remaining', 'Status', 'Pages this cycle']
  const body = rows.map(r => [
    r.name, r.amount || '', r.amount ? r.alertAt : '', r.spent, r.projected, r.remaining ?? '', r.status.label, r.pages,
  ])
  const meta = [
    [],
    ['Plan', pool?.planName || ''],
    ['Plan fee', pool?.fee ?? ''],
    ['Allocated', summary?.allocated ?? ''],
    ['Unallocated', summary?.unallocated ?? ''],
    ['Over plan', summary?.overPlan ?? ''],
    ['Currency', pool?.currency?.code || 'GBP'],
  ]
  return [header, ...body, ...meta]
}
